<template>
    <v-dialog v-model="editResidenceTermsDialog" max-width="767" persistent>
        <template v-slot:activator="{ on, attrs }">
            <v-btn v-bind="attrs" v-on="on" @click="setResidenceTerm()" :color="successColor" icon>
                <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                        <v-icon v-bind="attrs" v-on="on">
                            mdi-pencil
                        </v-icon>
                    </template>
                    <span>Edit</span>
                </v-tooltip>
            </v-btn>
        </template>
        <v-card>
            <v-card-title>
                Edit Residence Term
            </v-card-title>
            <v-card-text>
                <v-form ref="editResidenceTerm" v-model="valid" lazy-validation>
                    <v-text-field v-model="currentResidenceTerm.term" label="Term" :rules="termRules" required>
                    </v-text-field>
                    <v-container fluid class="pa-0">
                        <v-row no-gutters>
                            <v-col cols="12" md="6" class="pt-0">
                                <v-menu ref="startDate" v-model="startDateMenu" :close-on-content-click="false"
                                    transition="scale-transition" offset-y max-width="290px" min-width="290px">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field v-model="currentResidenceTerm.startDate" id="startDate"
                                            :color="successColor" label="Term Start Date" name="startDate"
                                            prepend-icon="mdi-calendar-check" readonly :rules="startDateRules"
                                            v-bind="attrs" v-on="on">
                                        </v-text-field>
                                    </template>
                                    <v-date-picker v-model="currentResidenceTerm.startDate" :color="successColor"
                                        :max="currentResidenceTerm.endDate" no-title @input="startDateMenu = false"
                                        @change="setTerm">
                                    </v-date-picker>
                                </v-menu>
                            </v-col>
                            <v-col cols="12" md="6" class="pt-0">
                                <v-menu ref="endDate" v-model="endDateMenu" :close-on-content-click="false"
                                    transition="scale-transition" offset-y max-width="290px" min-width="290px">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field v-model="currentResidenceTerm.endDate" id="endDate"
                                            :color="dangerColor" label="Term End Date" name="endDate"
                                            prepend-icon="mdi-calendar-remove" readonly :rules="endDateRules"
                                            v-bind="attrs" v-on="on">
                                        </v-text-field>
                                    </template>
                                    <v-date-picker v-model="currentResidenceTerm.endDate" :color="dangerColor"
                                        :min="currentResidenceTerm.startDate" no-title @input="endDateMenu = false"
                                        @change="setTerm">
                                    </v-date-picker>
                                </v-menu>
                            </v-col>
                        </v-row>
                    </v-container>
                    <v-menu ref="moveInDate" v-model="moveInDateMenu" :close-on-content-click="false"
                        transition="scale-transition" offset-y max-width="290px" min-width="290px">
                        <template v-slot:activator="{ on, attrs }">
                            <v-text-field v-model="currentResidenceTerm.moveInDate" id="moveInDate"
                                :color="primaryColor" label="Move-In Date" name="moveInDate"
                                prepend-icon="mdi-calendar-heart" readonly :rules="moveInDateRules" v-bind="attrs"
                                v-on="on">
                            </v-text-field>
                        </template>
                        <v-date-picker v-model="currentResidenceTerm.moveInDate" :color="primaryColor" no-title
                            @input="moveInDateMenu = false">
                        </v-date-picker>
                    </v-menu>
                    <v-select v-model="currentResidenceTerm.active" :items="activeItems" label="Active"
                        :rules="activeRules" required>
                    </v-select>
                    <v-select v-model="currentResidenceTerm.default"
                        hint="Set as default choice for the Online Residence Application form" :items="defaultItems"
                        label="Default" persistent-hint :rules="defaultRules" required>
                    </v-select>
                    <v-textarea v-model="currentResidenceTerm.notes" auto-grow class="pt-5" label="Notes" outlined>
                    </v-textarea>
                </v-form>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn ref="confirmBtn" class="white--text" :color="successColor" :disabled="!valid"
                    @click="editResidenceTerm()">
                    Save
                </v-btn>
                <v-btn :color="warningColor" @click="closeDialog()">
                    Cancel
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
export default {
    name: 'residence-terms-edit',
    props: {
        item: {
            required: true,
            type: Object
        }
    },
    data() {
        return {
            activeItems: [{
                    text: 'Yes',
                    value: '1'
                },
                {
                    text: 'No',
                    value: '0'
                }
            ],
            activeRules: [
                v => !(v === "") || 'Active is required',
            ],
            currentResidenceTerm: '',
            defaultItems: [{
                    text: 'Yes',
                    value: '1'
                },
                {
                    text: 'No',
                    value: '0'
                }
            ],
            defaultRules: [
                v => !(v === "") || 'Default is required',
            ],
            editResidenceTermsDialog: false,
            endDateMenu: false,
            endDateRules: [
                v => !!v || 'End date is required',
            ],
            months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
            moveInDateMenu: false,
            moveInDateRules: [
                v => !!v || 'Move in date is required',
            ],
            termRules: [
                v => !!v || 'Term is required',
                v => (v && v.length >= 2) || 'Term must be more than 2 characters',
            ],
            startDateMenu: false,
            startDateRules: [
                v => !!v || 'Start date is required',
            ],
            valid: true
        }
    },
    methods: {
        closeDialog() {
            this.editResidenceTermsDialog = false;
        },
        editResidenceTerm() {
            const that = this;
            const formData = new FormData();
            let api = `https://${location.hostname}/cgi-bin/res-app-api/controller.api.pl?updateEntity`;

            if (this.$refs.editResidenceTerm.validate()) {
                // Parse array into string
                formData.append('data', JSON.stringify(this.currentResidenceTerm));
                formData.append('mode', 'updateEntity');
                formData.append('type', 'ResidenceTerms');

                // Loading icon
                this.$refs.confirmBtn.loading = true;

                // Post request
                fetch(api, {
                        method: 'POST',
                        body: formData
                    })
                    .then(function (response) {
                        // Return JSON response
                        return response.json();
                    })
                    .then(function (json) {
                        // Success result
                        if (json.result.match(/OK/i)) {
                            that.$emit('setNotification', '#1dc9b7', 'mdi-check-circle', `${that.currentResidenceTerm.term} has been successfully updated.`, true);
                        } else if (json.message.match(/UNAUTHORIZED/i)) {
                            that.$emit('setNotification', '#f64e60', 'mdi-alert-outline', `You are not authorized to perform this action. Please refresh your application.`, true);
                        } else {
                            that.$emit('setNotification', '#ffb822', 'mdi-alert-circle', `${that.currentResidenceTerm.term} could not be updated. Please contact your system administrator.`, true);
                        }

                        that.$emit('refreshData');
                        that.closeDialog();
                        that.$refs.confirmBtn.loading = false;
                    })
                    .catch(function () {
                        that.$emit('refreshData');
                        that.closeDialog();
                        that.$refs.confirmBtn.loading = false;
                        that.$emit('setNotification', '#f64e60', 'mdi-alert-outline', `An error occurred. Please contact your system administrator.`, true);
                    });
            }
        },
        setResidenceTerm() {
            this.currentResidenceTerm = {
                ...this.item
            };

            if (this.$refs.editResidenceTerm) {
                this.$refs.editResidenceTerm.resetValidation();
            }
        },
        setTerm() {
            if (this.currentResidenceTerm.startDate && this.currentResidenceTerm.endDate) {
                let endDate = this.currentResidenceTerm.endDate.split("-");
                let startDate = this.currentResidenceTerm.startDate.split("-");
                this.currentResidenceTerm.term = `${this.months[parseInt(startDate[1]) - 1]} ${startDate[0]} - ${this.months[parseInt(endDate[1]) - 1]} ${endDate[0]}`;
            } else if (this.currentResidenceTerm.startDate && !this.currentResidenceTerm.endDate) {
                let startDate = this.currentResidenceTerm.startDate.split("-");
                this.currentResidenceTerm.term = `${this.months[parseInt(startDate[1]) - 1]} ${startDate[0]} - Term end date`;
            } else if (this.currentResidenceTerm.endDate && !this.currentResidenceTerm.startDate) {
                let endDate = this.currentResidenceTerm.endDate.split("-");
                this.currentResidenceTerm.term = `Term start date - ${this.months[parseInt(endDate[1]) - 1]} ${endDate[0]}`;
            }
        }
    },
    computed: {
        dangerColor() {
            return '#f64e60';
        },
        primaryColor() {
            return '#5867dd';
        },
        successColor() {
            return '#1dc9b7';
        },
        warningColor() {
            return '#ffb822'
        }
    }
}
</script>